import { useEffect } from 'react';
import type { Capability } from '@aiai/shared';
import { useOrg } from '../org/useOrg';
import { useCapabilities } from '../hooks/useCapabilities';
import { FormField } from './FormField';

/**
 * The "Workspace" target field inside a create modal on a cross-workspace page.
 * Creating is inherently workspace-scoped (repos, token, board), so this picks
 * which workspace the create targets — a dropdown when the user can create in
 * more than one, a static line when there's only one to choose.
 *
 * Selection IS the active workspace: changing it calls setActiveOrg, which
 * re-scopes the interceptor (so the modal's repo list refetches for the new
 * workspace) and the create call. `capability` filters to workspaces the user
 * may actually create in; `active` (the modal's open state) drives the on-open
 * guard that snaps the active workspace to an eligible one.
 */
export function WorkspaceSelectField({
  capability,
  active,
}: {
  capability: Capability;
  active: boolean;
}) {
  const { activeOrg, setActiveOrg } = useOrg();
  const { workspacesWith } = useCapabilities();
  const eligible = workspacesWith(capability);

  // When the modal opens, ensure the active workspace is one the user can
  // create in — otherwise snap to the first eligible one. A no-op once aligned.
  useEffect(() => {
    if (!active || eligible.length === 0) return;
    if (!eligible.some((o) => o.id === activeOrg?.id)) {
      setActiveOrg(eligible[0].id);
    }
  }, [active, eligible, activeOrg?.id, setActiveOrg]);

  if (eligible.length === 0) return null;

  if (eligible.length === 1) {
    return (
      <FormField label="Workspace">
        <p className="text-sm text-ink">
          Creating in <span className="font-medium">{eligible[0].name}</span>
          <span className="ml-1.5 font-mono text-xs text-ink-faint">/{eligible[0].slug}</span>
        </p>
      </FormField>
    );
  }

  return (
    <FormField label="Workspace">
      <select
        className="input"
        value={activeOrg?.id ?? ''}
        onChange={(e) => setActiveOrg(e.target.value)}
      >
        {eligible.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name} (/{o.slug})
          </option>
        ))}
      </select>
    </FormField>
  );
}
